/**
 * DateRangeFilter
 * Återanvändbar datumintervall-väljare (Från datum – Till datum)
 * Används i rapportsidor: EnergyConsumption, SpeedCompliance, EcoDriving, etc.
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import DateInput from '@/components/common/DateInput';

export interface DateRangeFilterProps {
  startDate: string;
  endDate: string;
  onChange: (field: 'startDate' | 'endDate', value: string) => void;
  labels?: {
    from?: string;
    to?: string;
  };
  className?: string;
  /** Layout: inline = två fält i rad, full = varje fält tar full bredd */
  layout?: 'inline' | 'full';
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChange,
  labels,
  className = '',
  layout = 'inline',
}) => {
  const { t } = useTranslation();
  const fromLabel = labels?.from ?? t('common.fromDate') ?? 'Från datum';
  const toLabel = labels?.to ?? t('common.toDate') ?? 'Till datum';

  const inputClass =
    'w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault';

  if (layout === 'full') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">
            {fromLabel}
          </label>
          <DateInput value={startDate}
            onChange={(e) => onChange('startDate', e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-textSecondary mb-1">
            {toLabel}
          </label>
          <DateInput value={endDate}
            onChange={(e) => onChange('endDate', e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${className}`}>
      <div>
        <label className="block text-sm font-medium text-textSecondary mb-1">
          {fromLabel}
        </label>
        <DateInput value={startDate}
          onChange={(e) => onChange('startDate', e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-textSecondary mb-1">
          {toLabel}
        </label>
        <DateInput value={endDate}
          onChange={(e) => onChange('endDate', e.target.value)}
          className={inputClass}
        />
      </div>
    </div>
  );
};

export default DateRangeFilter;
